import React from 'react';
import { Card } from 'react-bootstrap';
import './bloodwork-anemia-status.css';
import { BloodworkAnalysis } from '../../models/BloodworkAnalysis';
import {
  computeI3,
  I3BurdenBand,
  I3SectionResult,
} from '../../models/ImmuneInflammatory';
import { ImmuneInflammatoryBurdenGauge } from '../charts/ImmuneInflammatoryBurdenGauge';

interface Props {
  bloodworkAnalysis: BloodworkAnalysis;
}

// The four burden bands. Colors match the green/yellow/orange/red bar palette;
// `dark` is a legible text shade used for the active band label in the gauge.
const BURDEN_META: Record<I3BurdenBand, { label: string; dark: string }> = {
  low: { label: 'LOW', dark: '#3fa34d' },
  mild: { label: 'MILD', dark: '#c79a00' },
  moderate: { label: 'MODERATE', dark: '#e3741a' },
  high: { label: 'HIGH', dark: '#d8472f' },
};

// Bright fills for the per-section severity bars (same four gauge zones the
// other sections use) + legible dark shades for the value labels.
const GAUGE_PALETTE = { green: '#78F880', yellow: '#FEFA83', orange: '#FFD37E', red: '#FC6552', gray: '#e3e3e3' };
const BAND_PALETTE = { green: '#3fa34d', yellow: '#c79a00', orange: '#e3741a', red: '#d8472f', gray: '#b7b7b7' };
const TRACK_BG = '#e9ecf1';
const TRACK_INSET = 'inset 0 0 0 1px rgba(0,0,0,0.06)';
const BAR_HEIGHT = 14;

// Trim a trailing ".0" so weighted scores read cleanly (22.5 stays, 25.0 → 25).
const fmt = (n: number): string => (Number.isInteger(n) ? String(n) : n.toFixed(1));

export class ImmuneInflammatoryBurdenPanel extends React.Component<Props> {
  // Parent uses this to skip rendering the section entirely when nothing was entered.
  static shouldRender(ba: BloodworkAnalysis): boolean {
    return !!ba && !!ba.bloodwork && computeI3(ba.bloodwork).anyEntered;
  }

  // The weighted-score hero — the standard semicircle gauge on the 0–250 burden
  // scale, with the band thresholds (48 / 86 / 120) as tick labels and the score
  // + band in the center.
  renderHero(weightedTotal: number, weightedMax: number, rawTotal: number, band: I3BurdenBand) {
    const active = BURDEN_META[band];
    return (
      <div style={{ marginBottom: '10px', textAlign: 'center' }}>
        <ImmuneInflammatoryBurdenGauge
          value={weightedTotal}
          scoreText={`${fmt(weightedTotal)} / ${weightedMax}`}
          bandLabel={active.label}
          bandColor={active.dark}
        />
        <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>
          Raw total {rawTotal} / 224
        </div>
      </div>
    );
  }

  // Per-section severity bars, worst → best. Length = % of the section max,
  // hue = zone; unentered sections drop to gray "—" at the bottom.
  renderSectionBars(sections: I3SectionResult[]) {
    const ordered = [...sections].sort((a, b) => {
      if (a.hasValue !== b.hasValue) return a.hasValue ? -1 : 1;
      return b.pct - a.pct;
    });
    return (
      <div>
        <div style={{ textAlign: 'center', fontSize: '13px', fontWeight: 700, color: '#1a1a1a', marginBottom: '8px' }}>
          Section Breakdown
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
          {ordered.map(s => {
            const assessed = s.hasValue;
            const pct = Math.round(s.pct);
            return (
              <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 6px' }}>
                <span
                  style={{
                    width: '210px',
                    flexShrink: 0,
                    fontSize: '11.5px',
                    fontWeight: 600,
                    color: assessed ? '#333' : BAND_PALETTE.gray,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                  title={s.label}
                >
                  {s.label}
                </span>
                <div
                  style={{
                    position: 'relative',
                    flex: 1,
                    height: `${BAR_HEIGHT}px`,
                    borderRadius: '4px',
                    background: TRACK_BG,
                    boxShadow: TRACK_INSET,
                  }}
                >
                  {assessed && (
                    <div
                      style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: `${s.pct}%`,
                        minWidth: s.pct > 0 ? '6px' : '0',
                        background: GAUGE_PALETTE[s.zone],
                        borderRadius: '4px',
                      }}
                    />
                  )}
                  {assessed && s.pct > 0 && (
                    <span
                      style={{
                        position: 'absolute',
                        left: `${s.pct}%`,
                        top: '-1px',
                        bottom: '-1px',
                        width: '2px',
                        borderRadius: '1px',
                        transform: 'translateX(-100%)',
                        background: BAND_PALETTE[s.zone],
                      }}
                    />
                  )}
                </div>
                <span
                  style={{
                    width: '52px',
                    flexShrink: 0,
                    textAlign: 'right',
                    fontSize: '11px',
                    fontWeight: 600,
                    color: assessed ? BAND_PALETTE[s.zone] : BAND_PALETTE.gray,
                  }}
                >
                  {assessed ? `${s.raw}/${s.max}` : '—'}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  render() {
    const { bloodworkAnalysis } = this.props;
    if (!bloodworkAnalysis || !bloodworkAnalysis.bloodwork) return null;

    const result = computeI3(bloodworkAnalysis.bloodwork);
    if (!result.anyEntered) return null;

    return (
      <Card className="evaluationPanel i3EvaluationPanel" id="immune-inflammatory-burden-insights">
        <Card.Header className="evaluationPanelHeading">Weighted I³ Score</Card.Header>
        <Card.Body>
          <div className="anemiaStatusContainer">
            {this.renderHero(result.weightedTotal, result.weightedMax, result.rawTotal, result.band)}
            {this.renderSectionBars(result.sections)}
            <div style={{ textAlign: 'center', fontSize: '10.5px', color: '#999', marginTop: '10px' }}>
              Screening tool only — not a diagnostic instrument.
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  }
}

export default ImmuneInflammatoryBurdenPanel;
